import React, { useEffect, useState } from "react";
import { useAuth } from "../../provider/AuthProvider";
import { useDispatch } from "react-redux";
import { getTourAvailPrice } from "@/src/actions/tour/tourAction";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Info,
    Check,
    ChevronDown,
    ChevronUp,
    MapPin,
    Flag
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Paths } from "@/src/constants/Paths";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Login from "../../pages/Login";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { ChevronDownIcon } from "lucide-react";

// Custom DatePicker component adapted from TourBooking.jsx
function GroupDatePicker({ value, onChange, placeholder = "Select date", className }) {
    const [open, setOpen] = React.useState(false);

    const handleSelect = (newDate) => {
        setOpen(false);
        if (newDate) {
            onChange(newDate);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn(
                        "w-full justify-between font-normal",
                        !value && "text-muted-foreground",
                        className
                    )}
                >
                    {value ? format(value, "PPP") : placeholder}
                    <ChevronDownIcon className="h-4 w-4 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={value}
                    onSelect={handleSelect}
                    disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                    }
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );
}

// Helper to format date like "20 May 2026"
function formatDate(dateValue) {
    if (!dateValue) return "";
    try {
        const d = new Date(dateValue);
        if (Number.isNaN(d.getTime())) return String(dateValue);
        return format(d, "dd MMM yyyy");
    } catch (e) {
        return String(dateValue);
    }
}

function formatDayMonth(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "";
    return {
        day: d.toLocaleDateString("en-GB", { day: "numeric" }),
        month: d.toLocaleDateString("en-GB", { month: "short" })
    };
}


function getMonthYear(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "";
    // Use local date components to avoid UTC timezone conversion issues
    const year = d.getFullYear();
    const month = d.getMonth() + 1; // getMonth() is 0-indexed
    const isoKey = `${year}-${String(month).padStart(2, '0')}`;
    return {
        month: d.toLocaleDateString("en-GB", { month: "short" }),
        year: d.toLocaleDateString("en-GB", { year: "numeric" }),
        full: d.toLocaleDateString("en-GB", { month: "long", year: "numeric" }),
        iso: isoKey
    };
}

function addDays(dateStr, days) {
    if (!dateStr || !days) return dateStr;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    d.setDate(d.getDate() + parseInt(days, 10));
    return d.toISOString();
}

const GroupPricingCard = ({
    item,
    idx,
    packageTitle,
    packageId,
    durationNum,
    startLocation,
    endLocation,
    destination,
    handleBookClick
}) => {
    const [selectedDate, setSelectedDate] = useState(null);
    const uniqueId = item.TourPricingID || `group-${idx}`;
    const totalPrice = (item.TourFare || 0) + (item.FlightFare || 0);
    const groupSizeFrom = item.GroupSizeFrom || 1;
    const groupSizeTo = item.GroupSizeTo || "+";

    return (
        <Card key={uniqueId} className={cn("overflow-hidden border-l-4 transition-all border-l-transparent hover:border-l-primary")}>
            <CardContent className="p-0">
                <div className="p-4 sm:p-6 grid gap-4 grid-cols-1 md:grid-cols-4 items-center">
                    <div className="md:col-span-1 space-y-1">
                        <div className="text-xs text-muted-foreground">
                            Group Size
                        </div>
                        <div className="font-bold text-base">{groupSizeFrom} - {groupSizeTo} Pax</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                            Custom departure date
                        </div>
                    </div>

                    <div className="md:col-span-1 md:border-l md:pl-6 space-y-1">
                        <div className="text-xs text-muted-foreground">Tour Type</div>
                        <div className="font-bold text-base">Group Tour</div>
                        <div className="text-xs text-muted-foreground">Flexible schedule</div>
                    </div>

                    <div className="md:col-span-1 md:border-l md:pl-6 space-y-1">
                        <div className="text-xs text-muted-foreground">Price per person</div>
                        <div className="text-xl font-bold text-primary">NPR {totalPrice.toLocaleString()}</div>
                    </div>

                    <div className="md:col-span-1 text-right space-y-2 flex flex-col items-end">
                        <div className="flex flex-col items-center">
                            {/* <div className="text-xs text-muted-foreground">Select Date</div> */}
                            <GroupDatePicker className="w-40" value={selectedDate} onChange={(date) => setSelectedDate(date)} />
                            <div className="text-xs text-muted-foreground">Select Departure Date</div>
                        </div>
                    </div>
                    <Button
                        className="w-full md:w-auto px-4"
                        onClick={(e) => {
                            const returnDateRaw = selectedDate && durationNum > 0 ? addDays(selectedDate, durationNum - 1) : null;
                            handleBookClick(e, {
                                packageTitle,
                                departureDate: selectedDate ? formatDate(selectedDate) : null,
                                returnDate: returnDateRaw ? formatDate(returnDateRaw) : null,
                                pricePerPerson: totalPrice,
                                tourId: packageId,
                                pricingId: uniqueId,
                                tourType: "Group Tour",
                                image: destination?.ImageUrl,
                                departureDateRaw: selectedDate ? selectedDate.toISOString() : null,
                                startplace: startLocation,
                                endplace: endLocation,
                            });
                        }}
                    >
                        Request Booking
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default function TourAvailability({ packageId, durationDays, packageTitle, itineraries, destination, initialData }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [availOptions, setAvailOptions] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState("ALL");
    const [expandedIds, setExpandedIds] = useState({});
    const [viewMode, setViewMode] = useState("fixed");
    const { token } = useAuth();
    const [showLoginForm, setShowLoginForm] = useState(false);
    const [savedBookingState, setSavedBookingState] = useState(null);

    const handleBookClick = (e, bookingState) => {
        if (e) e.stopPropagation();
        if (!token) {
            setSavedBookingState(bookingState);
            setShowLoginForm(true);
        } else {
            navigate(Paths.TOUR_BOOKING.replace(":packageId", packageId).replace(":pricingId", bookingState.pricingId), {
                state: bookingState
            });
        }
    };

    // Parse numeric duration from string like "5" or "5 Days"
    // Or calculate from itineraries if available (User request)
    const durationNum = React.useMemo(() => {
        if (itineraries && itineraries.length > 0) {
            const total = itineraries.reduce((acc, item) => acc + (item.TotalDays || 0), 0);
            if (total > 0) return total;
        }

        if (!durationDays) return 0;
        const n = parseInt(durationDays, 10);
        return isNaN(n) ? 0 : n;
    }, [durationDays, itineraries]);

    const startLocation = React.useMemo(() => {
        if (itineraries && itineraries.length > 0) {
            return itineraries[0].Destination;
        }
        return "";
    }, [itineraries]);

    const endLocation = React.useMemo(() => {
        if (itineraries && itineraries.length > 0) {
            return itineraries[itineraries.length - 1].Destination;
        }
        return "";
    }, [itineraries]);

    // Parse available months from fixed departure data only (IsGroup === false)
    const months = React.useMemo(() => {
        if (!availOptions || availOptions.length === 0) return [];
        const mSet = new Set();
        const mList = [];

        availOptions.forEach(opt => {
            if (!opt.DepartureDate) return;
            const my = getMonthYear(opt.DepartureDate);
            if (my && !mSet.has(my.iso)) {
                mSet.add(my.iso);
                mList.push(my);
            }
        });
        return mList.sort((a, b) => a.iso.localeCompare(b.iso));
    }, [availOptions]);

    useEffect(() => {
        if (initialData && Array.isArray(initialData)) {
            setAvailOptions(initialData);
            setLoading(false);
            return;
        }

        if (packageId) {
            setLoading(true);
            getTourAvailPrice(packageId, (data) => {
                setLoading(false);
                if (data && Array.isArray(data)) {
                    setAvailOptions(data);
                } else if (data) {
                    setAvailOptions([data]);
                }
            });
        }
    }, [packageId, initialData]);

    // Filter items for Fixed Departures (IsGroup = false)
    const filteredItems = React.useMemo(() => {
        const fixedDepartures = availOptions.filter(item => !!item.DepartureDate && item.IsGroup === false);

        if (selectedMonth === "ALL") return fixedDepartures;
        return fixedDepartures.filter(item => {
            const my = getMonthYear(item.DepartureDate);
            return my.iso === selectedMonth;
        });
    }, [availOptions, selectedMonth]);

    // Filter items for Group Pricing (IsGroup = true)
    const groupPricingItems = React.useMemo(() => {
        return availOptions.filter(item => item.IsGroup === true);
    }, [availOptions]);

    const toggleExpand = (id) => {
        setExpandedIds(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    const handleSearchTypeChange = (value) => {
        setSearchType(value);
    };
    const [searchType, setSearchType] = React.useState("fixed");

    if (loading) {
        return <div className="py-8 text-center text-muted-foreground">Loading availability...</div>;
    }

    if (!availOptions || availOptions.length === 0) {
        return <div className="py-8 text-center text-muted-foreground">No availability found for this package.</div>;
    }

    if (filteredItems.length === 0 && availOptions.length > 0) {
        const hasGroupPricing = availOptions.some(item => !item.DepartureDate);
        return (
            <div className="py-8 text-center text-muted-foreground">
                {hasGroupPricing ? "Contact us for group pricing availability." : "No fixed departure dates found."}
            </div>
        );
    }

    return (
        <div className="space-y-6 pt-6">
            <div className="space-y-1">
                <h2 className="text-2xl font-bold">Tour Availability</h2>
                <p className="text-muted-foreground">Select your preferred departure date and secure your spot</p>
            </div>

            {/* View Mode Toggle - Pill Style */}
            {/* {(filteredItems.length > 0 || groupPricingItems.length > 0) && (
                <div className="inline-flex rounded-full bg-muted p-1">
                    <button
                        type="button"
                        onClick={() => setViewMode("fixed")}
                        className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === "fixed"
                            ? "bg-primary text-primary-foreground shadow-md"
                            : "text-muted-foreground hover:bg-muted-foreground/10"
                            }`}
                    >
                        Fix Date
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode("group")}
                        className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === "group"
                            ? "bg-primary text-primary-foreground shadow-md"
                            : "text-muted-foreground hover:bg-muted-foreground/10"
                            }`}
                    >
                        Custom Date
                    </button>
                </div>
            )} */}
            <RadioGroup
                onValueChange={handleSearchTypeChange}
                defaultValue={searchType}
                className="flex flex-col sm:flex-row sm:flex-wrap gap-3"
            >

                <RadioGroupItem
                    value={"fixed"}
                    id={"fixedDeparture"}
                />

                <Label
                    className="text-base cursor-pointer text-sm tracking-tight"
                    htmlFor={"fixedDeparture"}
                >
                    Fix Date
                </Label>

                <RadioGroupItem
                    value={"group"}
                    id={"customDeparture"}
                />

                <Label
                    className="text-base cursor-pointer text-sm tracking-tight"
                    htmlFor={"customDeparture"}
                >
                    Custom Date
                </Label>
            </RadioGroup>

            {/* Month Filter - Only for Fixed Departure view */}
            {/* {viewMode === "fixed" && ( */}
            {searchType === "fixed" && (
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    <Button
                        variant={selectedMonth === "ALL" ? "default" : "outline"}
                        className={cn("flex flex-col h-16 w-20 shrink-0 gap-0", selectedMonth === "ALL" ? "px-2 lg:px-4" : "")}
                        onClick={() => setSelectedMonth("ALL")}
                    >
                        <span className="text-sm font-bold">All</span>
                        <span className="text-xs font-normal">Dates</span>
                    </Button>
                    {months.map((m) => (
                        <Button
                            key={m.iso}
                            variant={selectedMonth === m.iso ? "default" : "outline"}
                            className={cn("flex flex-col h-16 w-20 shrink-0 gap-0", selectedMonth === m.iso ? "px-2 lg:px-4" : "")}
                            onClick={() => setSelectedMonth(m.iso)}
                        >
                            <span className="text-sm font-bold">{m.month}</span>
                            <span className="text-xs font-normal">{m.year}</span>
                        </Button>
                    ))}
                </div>
            )}

            {/* Availability List - Fixed Departure */}
            {/* {viewMode === "fixed" && filteredItems.length > 0 ? ( */}
            {searchType === "fixed" && filteredItems.length > 0 ? (
                <div className="space-y-3">
                    {filteredItems.map((item, idx) => {
                        const uniqueId = item.TourPricingID || idx;
                        const isExpanded = !!expandedIds[uniqueId];

                        const startOriginal = item.DepartureDate;
                        const endOriginal = durationNum > 0 ? addDays(startOriginal, durationNum - 1) : startOriginal;

                        const startDate = formatDate(startOriginal);
                        const endDate = formatDate(endOriginal);

                        const startDM = formatDayMonth(startOriginal);
                        const endDM = formatDayMonth(endOriginal);

                        const seats = item.FixDepartureSeats ?? "On Request";
                        const totalPrice = (item.TourFare || 0) + (item.FlightFare || 0);
                        const isGroup = item.IsGroup;

                        return (
                            <Card key={uniqueId} className={cn("overflow-hidden border-l-4 transition-all", isExpanded ? "border-l-primary ring-1 ring-primary/20" : "border-l-transparent hover:border-l-primary")}>
                                <CardContent className="p-0">
                                    {/* Header Row - Clickable */}
                                    <div
                                        className="p-4 sm:p-6 grid gap-4 grid-cols-2 md:grid-cols-4 items-center cursor-pointer"
                                        onClick={() => toggleExpand(uniqueId)}
                                    >
                                        <div className="col-span-2 md:col-span-1 space-y-1">
                                            <div className="text-xs text-muted-foreground">
                                                From {startLocation || "Start"} To {endLocation || "End"}
                                            </div>
                                            <div className="font-bold text-base">{startDate}</div>
                                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                                Departure Guaranteed <Info className="w-3 h-3" />
                                            </div>
                                        </div>

                                        <div className="col-span-2 md:col-span-1 md:border-l md:pl-6 space-y-1">
                                            <div className="text-xs text-muted-foreground hidden md:block">&nbsp;</div>
                                            <div className="font-bold text-base">{endDate}</div>
                                            <div className="hidden text-xs text-muted-foreground md:block">&nbsp;</div>
                                        </div>

                                        <div className="col-span-1 md:col-span-1 space-y-1">
                                            {isExpanded ? (
                                                <div className="text-xs font-medium text-orange-500 flex items-center gap-1">
                                                    Seats remaining: {seats}
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="text-xs text-muted-foreground">Seats remaining</div>
                                                    <div className="font-bold">{seats}</div>
                                                </>
                                            )}
                                        </div>

                                        <div className="col-span-1 md:col-span-1 text-right space-y-1 flex flex-col items-end">
                                            <div className="flex items-center gap-2">
                                                <div className="flex flex-col items-end">
                                                    <span className="text-xl font-bold">NPR {totalPrice.toLocaleString()}</span>
                                                </div>
                                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                            </div>
                                            <div className="text-xs text-green-600 flex items-center justify-end gap-1">
                                                <Button className="px-2 lg:px-4" onClick={(e) => {
                                                    handleBookClick(e, {
                                                        packageTitle,
                                                        departureDate: startDate,
                                                        returnDate: endDate,
                                                        pricePerPerson: totalPrice,
                                                        tourId: packageId,
                                                        pricingId: uniqueId,
                                                        tourType: isGroup ? "Group Tour" : "Fixed Departure",
                                                        image: destination?.ImageUrl,
                                                        departureDateRaw: startOriginal,
                                                        startplace: startLocation,
                                                        endplace: endLocation,
                                                    });
                                                }}> Instant Book</Button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Details */}
                                    {isExpanded && (
                                        <div className="px-6 pb-6 pt-2 border-t border-border/50 animate-in slide-in-from-top-2 duration-200">
                                            <div className="grid md:grid-cols-2 gap-6">
                                                {/* Left: Journey Details */}
                                                <div className="space-y-4">
                                                    <h3 className="font-bold text-lg">{packageTitle}</h3>
                                                    {/* <div className="flex items-center text-xs text-blue-500 cursor-pointer hover:underline">
                                                        Multiple Rooms <Info className="ml-1 h-3 w-3" />
                                                    </div> */}

                                                    <div className="relative pl-6 space-y-6">
                                                        {/* Vertical Line */}
                                                        <div className="absolute left-[9px] top-2 bottom-6 w-0.5 bg-border border-l border-dashed border-gray-300"></div>

                                                        {/* Start Point */}
                                                        <div className="relative">
                                                            <div className="absolute -left-[27px] -top-1">
                                                                <div className="bg-green-100 p-1 rounded-full">
                                                                    <MapPin className="h-4 w-4 text-green-600" />
                                                                </div>
                                                            </div>

                                                            <div className="flex items-start gap-4">
                                                                <div className="flex flex-col items-center min-w-[30px]">
                                                                    <span className="text-xl font-bold leading-none">{startDM.day}</span>
                                                                    <span className="text-xs uppercase font-medium text-muted-foreground">{startDM.month}</span>
                                                                </div>
                                                                <div>
                                                                    <div className="font-semibold">Tour start</div>
                                                                    <div className="text-sm text-muted-foreground">{startLocation}</div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* End Point */}
                                                        <div className="relative">
                                                            <div className="absolute -left-[27px] -top-1">
                                                                <div className="bg-red-100 p-1 rounded-full">
                                                                    <Flag className="h-4 w-4 text-red-600 fill-current" />
                                                                </div>
                                                            </div>
                                                            <div className="flex items-start gap-4">
                                                                <div className="flex flex-col items-center min-w-[30px]">
                                                                    <span className="text-xl font-bold leading-none">{endDM.day}</span>
                                                                    <span className="text-xs uppercase font-medium text-muted-foreground">{endDM.month}</span>
                                                                </div>
                                                                <div>
                                                                    <div className="font-semibold">Tour end</div>
                                                                    <div className="text-sm text-muted-foreground">{endLocation}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right: Pricing & CTA */}
                                                <div className="flex flex-col justify-center items-center md:items-end space-y-4 border-t md:border-t-0 md:border-l pt-4 md:pt-0 md:pl-6">
                                                    <div className="text-center md:text-right">
                                                        {/* <div className="text-sm text-muted-foreground mb-1">
                                                            Price <span className="line-through text-xs ml-1">NPR {Math.round(totalPrice * 1.2).toLocaleString()}</span>
                                                        </div> */}
                                                        <div className="text-3xl font-bold">NPR {totalPrice.toLocaleString()}</div>
                                                    </div>

                                                    <Button
                                                        //className="w-full md:w-auto min-w-[200px] bg-green-600 hover:bg-green-700 text-white font-bold h-12 text-lg"
                                                        className="px-2 lg:px-4 w-full md:w-auto min-w-[200px]"
                                                        onClick={(e) => {
                                                            handleBookClick(e, {
                                                                packageTitle,
                                                                departureDate: startDate,
                                                                returnDate: endDate,
                                                                pricePerPerson: totalPrice,
                                                                tourId: packageId,
                                                                pricingId: uniqueId,
                                                                tourType: isGroup ? "Group Tour" : "Fixed Departure",
                                                                image: destination?.ImageUrl,
                                                                departureDateRaw: startOriginal,
                                                                startplace: startLocation,
                                                                endplace: endLocation,
                                                            });
                                                        }}
                                                    >
                                                        Instant Book
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
                // ) : viewMode === "fixed" ? (
            ) : searchType === "fixed" ? (
                <div className="text-muted-foreground italic py-4">No fixed departure dates available.</div>
            ) : null}

            {/* Group Pricing Section */}
            {/* {viewMode === "group" && groupPricingItems.length > 0 && ( */}
            {/* {searchType === "group" && groupPricingItems.length > 0 && (
                <div className="space-y-3">
                    {groupPricingItems.map((item, idx) => {
                        const uniqueId = item.TourPricingID || `group-${idx}`;
                        const totalPrice = (item.TourFare || 0) + (item.FlightFare || 0);
                        const groupSizeFrom = item.GroupSizeFrom || 1;
                        const groupSizeTo = item.GroupSizeTo || "+";

                        return (
                            <Card key={uniqueId} className={cn("overflow-hidden border-l-4 transition-all border-l-transparent hover:border-l-primary")}>
                                <CardContent className="p-0">
                                    <div className="p-4 sm:p-6 grid gap-4 grid-cols-2 md:grid-cols-4 items-center">
                                        <div className="col-span-2 md:col-span-1 space-y-1">
                                            <div className="text-xs text-muted-foreground">
                                                Group Size
                                            </div>
                                            <div className="font-bold text-base">{groupSizeFrom} - {groupSizeTo} Pax</div>
                                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                                Custom departure date
                                            </div>
                                        </div>

                                        <div className="col-span-2 md:col-span-1 md:border-l md:pl-6 space-y-1">
                                            <div className="text-xs text-muted-foreground">Tour Type</div>
                                            <div className="font-bold text-base">Group Tour</div>
                                            <div className="text-xs text-muted-foreground">Flexible schedule</div>
                                        </div>

                                        <div className="col-span-1 md:col-span-1 space-y-1">
                                            <div className="text-xs text-muted-foreground">Price per person</div>
                                            <div className="font-bold text-xl text-primary">NPR {totalPrice.toLocaleString()}</div>
                                        </div>

                                        <div className="col-span-1 md:col-span-1 text-right space-y-1 flex flex-col items-end">
                                            <Button
                                                className="px-4"
                                                onClick={(e) => {
                                                    handleBookClick(e, {
                                                        packageTitle,
                                                        departureDate: null,
                                                        returnDate: null,
                                                        pricePerPerson: totalPrice,
                                                        tourId: packageId,
                                                        pricingId: uniqueId,
                                                        tourType: "Group Tour",
                                                        image: destination?.ImageUrl,
                                                        departureDateRaw: null,
                                                        startplace: startLocation,
                                                        endplace: endLocation,
                                                    });
                                                }}
                                            >
                                                Request Booking
                                            </Button>
                                        </div>
                                    </div>  
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )} */}
            {searchType === "group" && groupPricingItems.length > 0 && (
                <div className="space-y-3">
                    {groupPricingItems.map((item, idx) => (
                        <GroupPricingCard
                            key={item.TourPricingID || `group-${idx}`}
                            item={item}
                            idx={idx}
                            packageTitle={packageTitle}
                            packageId={packageId}
                            durationNum={durationNum}
                            startLocation={startLocation}
                            endLocation={endLocation}
                            destination={destination}
                            handleBookClick={handleBookClick}
                        />
                    ))}
                </div>
            )}
            {/* Login Dialog */}
            <Dialog open={showLoginForm} onOpenChange={setShowLoginForm}>
                <DialogContent>
                    <Login
                        fromLogin
                        handleClose={(newToken) => {
                            setShowLoginForm(false);
                            if (newToken && savedBookingState) {
                                navigate(Paths.TOUR_BOOKING.replace(":packageId", packageId).replace(":pricingId", savedBookingState.pricingId), {
                                    state: savedBookingState
                                });
                            }
                        }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}
